import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { palette } from '@livable/saas-react-components';
import { makeStyles } from '@mui/styles';
const border = "1px solid ".concat(palette.shades[300]);
const tableText = {
  color: palette.shades[800],
  fontFamily: palette.fontFamilies.source,
  fontSize: '13px',
  lineHeight: 'normal'
};

/** Styles for the suggested allocation table (Figma: allocation table, view and edit states). */
const useStyles = makeStyles({
  panel: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    padding: '0 16px 12px'
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    // Cancels the panel padding, so the divider spans the whole panel as in the design.
    margin: '0 -16px',
    padding: '12px 16px',
    borderBottom: border
  },
  title: {
    color: palette.shades[800],
    fontFamily: palette.fontFamilies.poppins,
    fontSize: '13px',
    fontWeight: 600
  },
  /** A table that adds up several services on the bill says which, under its title (LB-6015). */
  parts: _objectSpread(_objectSpread({}, tableText), {}, {
    padding: '8px 0 0',
    color: palette.shades[700]
  }),
  statusChip: {
    flexShrink: 0,
    padding: '2px 6px',
    borderRadius: '2px',
    color: palette.shades[700],
    backgroundColor: palette.shades[300],
    fontFamily: palette.fontFamilies.poppins,
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: '16px',
    whiteSpace: 'nowrap'
  },
  statusChipDone: {
    color: palette.success.dark,
    backgroundColor: palette.success.light
  },
  statusChipMuted: {
    color: palette.warning.dark,
    backgroundColor: palette.warning.light
  },
  summary: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 0',
    borderBottom: border,
    flexWrap: 'wrap'
  },
  summaryItem: _objectSpread(_objectSpread({}, tableText), {}, {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    whiteSpace: 'nowrap'
  }),
  summaryValue: {
    fontWeight: 700
  },
  /** The AppFolio charge on date of the table (LB-6118), under its figures, as Apply Billing shows it under the bill's. */
  chargeOn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 0',
    borderBottom: border
  },
  chargeOnNote: _objectSpread(_objectSpread({}, tableText), {}, {
    color: palette.shades[600]
  }),
  chargeOnClear: {
    width: '40px',
    minWidth: '40px',
    height: '40px'
  },
  summaryDivider: {
    alignSelf: 'stretch',
    width: '1px',
    backgroundColor: palette.shades[300]
  },
  row: _objectSpread(_objectSpread({}, tableText), {}, {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '8px 0',
    borderBottom: border
  }),
  /**
   * The allocation table's headers (LB-5509) are long for columns sized to the figures under them, so they
   * wrap, a size down from the rows: that keeps the table inside the bill details pane down to a 1280px
   * window, where the pane leaves it about 380px.
   */
  rowHead: {
    alignItems: 'flex-end',
    fontSize: '12px',
    fontWeight: 600,
    '& > span': {
      whiteSpace: 'normal',
      // The headers wrap rather than truncate, so nothing in them is cut off.
      overflow: 'visible'
    }
  },
  rowLast: {
    borderBottom: 'none'
  },
  /** Units not billable yet still take part in the split, but they are not the point of the table. */
  rowMuted: {
    color: palette.shades[700]
  },
  unbillableDivider: _objectSpread(_objectSpread({}, tableText), {}, {
    padding: '6px 0',
    borderBottom: border,
    backgroundColor: palette.shades[100],
    color: palette.shades[700],
    textAlign: 'center'
  }),
  /**
   * Unit and resident share what the figures leave, two parts to three, and each stays on one line with the rest on
   * hover. A fixed 44px unit column wrapped a name like "Sigrid Barton Sr. - 1648" onto four lines (LB-6036); this
   * truncates it instead, and gives it more room wherever the pane is wider than at a 1280px window.
   */
  colUnit: {
    flex: '2 1 0',
    minWidth: '44px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis'
  },
  colResident: {
    flex: '3 1 0',
    minWidth: '64px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis'
  },
  colOccupants: {
    flexShrink: 0,
    width: '56px',
    textAlign: 'right'
  },
  colFactor: {
    flexShrink: 0,
    width: '58px',
    textAlign: 'right'
  },
  colDays: {
    flexShrink: 0,
    width: '44px',
    textAlign: 'right'
  },
  colTotal: {
    flexShrink: 0,
    width: '68px',
    fontWeight: 600,
    textAlign: 'right'
  },
  /**
   * Sticks to the bottom of the scrolling pane while the table above it is on screen, so Allocate or Approve is in
   * reach on a table of hundreds of units without scrolling past every one (LB-6036). It settles back under the table
   * once the table ends. A scrolling pane with bottom padding sets `--allocation-actions-sticky-bottom` to minus that
   * padding, or the button stops short of the pane's edge.
   */
  actions: {
    position: 'sticky',
    bottom: 'var(--allocation-actions-sticky-bottom, 0px)',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '8px',
    // Cancels the panel padding, so the band spans the whole panel, as the title row does.
    margin: '0 -16px -12px',
    padding: '8px 16px',
    borderTop: border,
    backgroundColor: palette.shades[100]
  },
  message: _objectSpread(_objectSpread({}, tableText), {}, {
    padding: '12px 0',
    color: palette.shades[700]
  }),
  skeletonRow: {
    height: '33px',
    borderBottom: border
  },
  /** The bill covers days already allocated for this utility, so its charges cannot be approved. */
  warningBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    margin: '12px 0 0',
    padding: '8px 12px',
    borderRadius: '4px',
    backgroundColor: palette.warning.light,
    '& .MuiSvgIcon-root': {
      flexShrink: 0,
      width: '20px',
      height: '20px',
      fontSize: '20px'
    }
  },
  warningText: {
    color: palette.warning.dark,
    fontFamily: palette.fontFamilies.source,
    fontSize: '13px',
    lineHeight: 1.3
  },
  /** The bill rose sharply on the last one, so its comparison has to be reviewed before approval. */
  reviewBox: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '12px',
    margin: '12px 0 0',
    padding: '12px',
    borderRadius: '4px',
    backgroundColor: palette.warning.light
  },
  reviewText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  reviewTitle: {
    color: palette.warning.dark,
    fontFamily: palette.fontFamilies.poppins,
    fontSize: '13px',
    fontWeight: 600
  },
  reviewCheckbox: {
    margin: '4px 0 0',
    '& .MuiFormControlLabel-label': _objectSpread(_objectSpread({}, tableText), {}, {
      color: palette.warning.dark
    })
  },
  reviewAction: {
    flexShrink: 0
  },
  comparison: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    // Nine columns of figures fit the wide modal once the cells stop padding them apart. The scroll
    // is left in for the narrow screens where they cannot fit however tight the cells are.
    overflowX: 'auto',
    '& .MuiTableCell-root': _objectSpread(_objectSpread({}, tableText), {}, {
      padding: '8px 10px',
      whiteSpace: 'nowrap',
      // The outer columns keep their distance from the edges rather than sitting against them.
      '&:first-child': {
        paddingLeft: '16px'
      },
      '&:last-child': {
        paddingRight: '16px'
      }
    }),
    '& .MuiTableCell-head': {
      color: palette.shades[700],
      fontWeight: 600
    }
  },
  /** Unresolved target: the bill names a property or utility the company setup does not pin down. */
  picker: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '12px 0'
  },
  pickerFields: {
    display: 'flex',
    gap: '12px',
    '@media (max-width: 599px)': {
      flexDirection: 'column'
    }
  },
  pickerField: {
    flex: '1 0 0',
    minWidth: 0,
    maxWidth: '420px'
  },
  editForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    paddingTop: '16px'
  },
  editRow: {
    display: 'flex',
    gap: '12px',
    '@media (max-width: 599px)': {
      flexDirection: 'column'
    }
  },
  editField: {
    flex: '1 0 0',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  extractedHint: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    color: palette.shades[700],
    fontFamily: palette.fontFamilies.source,
    fontSize: '13px',
    fontWeight: 600
  },
  extractedHintChip: {
    padding: '2px 6px',
    borderRadius: '2px',
    backgroundColor: palette.success.light,
    color: palette.success.dark,
    fontFamily: palette.fontFamilies.poppins,
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: '16px'
  },
  sectionLabel: {
    color: palette.shades[800],
    fontFamily: palette.fontFamilies.poppins,
    fontSize: '13px',
    fontWeight: 600
  },
  deductions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  deductionRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '4px 4px 4px 12px',
    borderRadius: '2px',
    backgroundColor: palette.shades[200]
  },
  deductionLabel: {
    color: palette.shades[700],
    fontFamily: palette.fontFamilies.poppins,
    fontSize: '13px',
    fontWeight: 600
  },
  deductionValue: {
    color: palette.shades[800],
    fontFamily: palette.fontFamilies.poppins,
    fontSize: '13px',
    fontWeight: 600
  },
  deductionInput: {
    width: '96px',
    backgroundColor: palette.shades[100],
    '& input': {
      padding: '6px 8px',
      textAlign: 'right'
    }
  },
  deductionLocked: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 12px 6px 8px',
    border,
    borderRadius: '2px',
    opacity: 0.5,
    color: palette.shades[800],
    fontFamily: palette.fontFamilies.poppins,
    fontSize: '13px',
    fontWeight: 600,
    '& .MuiSvgIcon-root': {
      width: '20px',
      height: '20px',
      fontSize: '20px'
    }
  }
});
export default useStyles;